import React, { useState, useEffect } from 'react';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import MobileHeader from '../components/MobileHeader';
import moment from 'moment';

const AttendanceScreen = () => {
  const [loading, setLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState([]);
  const [currentDate, setCurrentDate] = useState(moment());

  useEffect(() => {
    loadData(currentDate.format('MM'), currentDate.format('YYYY'));
  }, []);

  const loadData = async (month, year) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('userToken');
      const response = await api.get(
        `api/school/school-transaction/attendance/get-attendance-student?month=${month}&year=${year}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      setAttendanceData(response.data || []);
    } catch (error) {
      console.error('AttendanceScreen loadData error:', error);
      handleCatch(error);
      setAttendanceData([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevMonth = () => {
    const newDate = moment(currentDate).subtract(1, 'month');
    setCurrentDate(newDate);
    loadData(newDate.format('MM'), newDate.format('YYYY'));
  };

  const handleNextMonth = () => {
    const newDate = moment(currentDate).add(1, 'month');
    setCurrentDate(newDate);
    loadData(newDate.format('MM'), newDate.format('YYYY'));
  };

  const getAttendanceForDate = (date) => {
    const dateKey = moment(date).format('YYYY-MM-DD');
    return attendanceData.find(
      (item) => moment(item.attendanceDate).format('YYYY-MM-DD') === dateKey
    );
  };

  const renderCalendar = () => {
    const startOfMonth = moment(currentDate).startOf('month');
    const endOfMonth = moment(currentDate).endOf('month');
    const startDate = moment(startOfMonth).startOf('week');
    const endDate = moment(endOfMonth).endOf('week');

    const calendar = [];
    const day = moment(startDate);

    while (day.isSameOrBefore(endDate)) {
      calendar.push(moment(day));
      day.add(1, 'day');
    }

    const weeks = [];
    for (let i = 0; i < calendar.length; i += 7) {
      weeks.push(calendar.slice(i, i + 7));
    }

    return weeks;
  };

  const weeks = renderCalendar();
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
      <MobileHeader title="My Attendance" showBack={true} />

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
          backgroundColor: '#F5F7FA',
          p: 1,
        }}
      >
        {loading && attendanceData.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              backgroundColor: '#fff',
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
            }}
          >
            {/* Month navigation — 48px tall */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                height: 48,
                px: 1,
                borderBottom: '1px solid #f0f0f0',
                backgroundColor: '#fff',
              }}
            >
              <IconButton onClick={handlePrevMonth} size="small" sx={{ color: colors.BLUE }}>
                <ChevronLeftIcon />
              </IconButton>
              <Typography sx={{ fontWeight: 700, fontSize: 16, color: colors.BLUE }}>
                {currentDate.format('MMMM YYYY')}
              </Typography>
              <IconButton onClick={handleNextMonth} size="small" sx={{ color: colors.BLUE }}>
                <ChevronRightIcon />
              </IconButton>
            </Box>

            {/* Day name header row */}
            <Box sx={{ display: 'flex', backgroundColor: '#F5F7FA' }}>
              {dayNames.map((dayName) => (
                <Box
                  key={dayName}
                  sx={{
                    width: 'calc(100% / 7)',
                    textAlign: 'center',
                    fontSize: 11,
                    fontWeight: 700,
                    color: '#888',
                    py: 1,
                  }}
                >
                  {dayName}
                </Box>
              ))}
            </Box>

            {/* Calendar day cells */}
            <Box sx={{ px: 0.5, pb: 1 }}>
              {weeks.map((week, weekIndex) => (
                <Box key={weekIndex} sx={{ display: 'flex', mb: 0.5 }}>
                  {week.map((day) => {
                    const isCurrentMonth = day.month() === currentDate.month();
                    const attendance = getAttendanceForDate(day);
                    const isPresent = attendance?.attendance === 'P';
                    const isAbsent = attendance?.attendance === 'A';
                    const isToday = day.isSame(moment(), 'day');

                    return (
                      <Box
                        key={day.format('YYYY-MM-DD')}
                        sx={{
                          width: 'calc(100% / 7)',
                          height: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 1,
                          bgcolor: isPresent
                            ? '#1565C0'
                            : isAbsent
                            ? '#C62828'
                            : 'transparent',
                          color: isPresent || isAbsent
                            ? '#fff'
                            : isCurrentMonth
                            ? '#333'
                            : '#ccc',
                          fontWeight: isPresent || isAbsent ? 700 : 400,
                          fontSize: 13,
                          border: isToday ? `2px solid ${colors.BLUE}` : 'none',
                          boxSizing: 'border-box',
                        }}
                      >
                        {day.date()}
                      </Box>
                    );
                  })}
                </Box>
              ))}
            </Box>

            {/* Legend */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 3,
                py: 1.5,
                borderTop: '1px solid #f0f0f0',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: '#1565C0' }} />
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#555' }}>
                  Present
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 14, height: 14, borderRadius: 0.5, bgcolor: '#C62828' }} />
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#555' }}>
                  Absent
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box
                  sx={{
                    width: 14,
                    height: 14,
                    borderRadius: 0.5,
                    border: `2px solid ${colors.BLUE}`,
                    bgcolor: 'transparent',
                  }}
                />
                <Typography sx={{ fontSize: 12, fontWeight: 500, color: '#555' }}>
                  Today
                </Typography>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AttendanceScreen;
